import React, { useState, useEffect, useMemo } from 'react';
import { useLocation, useNavigate, Navigate } from 'react-router-dom';
import { Employee, EmployeeStatus, TimeLog, LeaveBalance } from '../../shared/types.js';
import { useTimeClock } from '../hooks/useTimeClock';
import Button from '../components/Button';
import { ChevronLeftIcon, FileTextIcon, LockIcon } from '../components/icons';
import { formatDuration } from '../utils';
import PINModal from '../components/PINModal';
import LeaveDetailsModal from '../components/LeaveDetailsModal';
import ManualTimeEntryModal from '../components/ManualTimeEntryModal';
import LeaveBalanceCard from '../components/LeaveBalanceCard';

const EmployeeDashboardScreen: React.FC = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const { handleClockAction, handleBreakAction, timeLogs, getGroupForEmployee, updateEmployee } = useTimeClock();

    const { employee, isAdmin } = (location.state || {}) as { employee: Employee; isAdmin?: boolean };

    const [currentStatus, setCurrentStatus] = useState(employee?.status);
    const [isLoading, setIsLoading] = useState(false);
    const [message, setMessage] = useState('');
    const [error, setError] = useState('');
    const [elapsedTime, setElapsedTime] = useState(0);
    const [isPinModalOpen, setIsPinModalOpen] = useState(false);
    const [isLeaveDetailsModalOpen, setIsLeaveDetailsModalOpen] = useState(false);
    const [selectedLeaveBalance, setSelectedLeaveBalance] = useState<LeaveBalance | null>(null);
    const [isManualTimeModalOpen, setIsManualTimeModalOpen] = useState(false);
    const [manualAction, setManualAction] = useState<null | 'clock-in' | 'clock-out' | 'break-start' | 'break-end'>(null);

    const employeeGroup = useMemo(() => {
        if (!employee) return null;
        return getGroupForEmployee(employee.id);
    }, [employee, getGroupForEmployee]);

    useEffect(() => {
        if (message) {
            const timer = setTimeout(() => {
                navigate('/');
            }, 2000);
            return () => clearTimeout(timer);
        }
    }, [message, navigate]);

    useEffect(() => {
        if (error) {
            const timer = setTimeout(() => {
                setError('');
            }, 3000);
            return () => clearTimeout(timer);
        }
    }, [error]);

    useEffect(() => {
        let intervalId: number | undefined;

        if (employee && currentStatus !== EmployeeStatus.CLOCKED_OUT) {
            const inProgressLog = timeLogs
                .filter(log => log.employeeId === employee.emp_number && !log.endTime)
                .sort((a,b) => b.startTime - a.startTime)[0];

            if (inProgressLog) {
                const startTime = inProgressLog.startTime;
                const update = () => {
                    setElapsedTime(Date.now() - startTime);
                };
                update();
                intervalId = window.setInterval(update, 1000);
            }
        } else {
            setElapsedTime(0);
        }

        return () => {
            if (intervalId) clearInterval(intervalId);
        };
    }, [currentStatus, employee, timeLogs]);

    if (!employee) {
        return <Navigate to="/" />;
    }

    const onClockInOut = async (manualTimestamp?: number) => {
        if (isAdmin && !manualTimestamp) {
            setManualAction(currentStatus === EmployeeStatus.CLOCKED_OUT ? 'clock-in' : 'clock-out');
            setIsManualTimeModalOpen(true);
            return;
        }

        setIsLoading(true);
        setError('');
        try {
            const newStatus = await handleClockAction(employee.id, manualTimestamp);
            setCurrentStatus(newStatus);
            setMessage(newStatus === EmployeeStatus.CLOCKED_IN ? 'Successfully Clocked In!' : 'Successfully Clocked Out!');
        } catch (e) {
            if (e instanceof Error) {
                setError(e.message);
            } else {
                setError('An unknown error occurred.');
            }
        } finally {
            setIsLoading(false);
        }
    };

    const onBreak = async (manualTimestamp?: number) => {
        if (isAdmin && !manualTimestamp) {
            setManualAction(currentStatus === EmployeeStatus.CLOCKED_IN ? 'break-start' : 'break-end');
            setIsManualTimeModalOpen(true);
            return;
        }

        setIsLoading(true);
        setError('');
        try {
            const newStatus = await handleBreakAction(employee.id, manualTimestamp);
            setCurrentStatus(newStatus);
            setMessage(newStatus === EmployeeStatus.ON_BREAK ? 'Lunch Started' : 'Lunch Ended');
        } catch (e) {
            if (e instanceof Error) {
                setError(e.message);
            } else {
                setError('An unknown error occurred while processing break.');
            }
        } finally {
            setIsLoading(false);
        }
    };

    const handleManualTimeSave = (timestamp: number) => {
        setIsManualTimeModalOpen(false);
        if (!manualAction) return;

        if (manualAction === 'clock-in' || manualAction === 'clock-out') {
            onClockInOut(timestamp);
        } else if (manualAction === 'break-start' || manualAction === 'break-end') {
            onBreak(timestamp);
        }
        setManualAction(null);
    };

    const getManualModalTitle = () => {
        switch (manualAction) {
            case 'clock-in': return 'Manual Clock In';
            case 'clock-out': return 'Manual Clock Out';
            case 'break-start': return 'Manual Lunch Start';
            case 'break-end': return 'Manual Lunch End';
            default: return 'Manual Time Entry';
        }
    };

    const handleSavePin = async (newPin: string) => {
        if (!employee) return;
        await updateEmployee({ ...employee, pin: newPin });
        setIsPinModalOpen(false);
    };
    
    const handleLeaveCardClick = (balance: LeaveBalance) => {
        setSelectedLeaveBalance(balance);
        setIsLeaveDetailsModalOpen(true);
    };

    const getClockButtonText = () => {
        switch (currentStatus) {
            case EmployeeStatus.CLOCKED_OUT:
                return 'Clock In';
            case EmployeeStatus.CLOCKED_IN:
            case EmployeeStatus.ON_BREAK:
                return 'Clock Out';
            default:
                return 'Clock In/Out';
        }
    };

    const getBreakButtonText = () => {
        switch (currentStatus) {
            case EmployeeStatus.CLOCKED_IN:
                return 'Start Lunch';
            case EmployeeStatus.ON_BREAK:
                return 'End Lunch';
            default:
                return 'Lunch';
        }
    }

    const showLunchButton = employeeGroup?.settings.breaksAndLunches.showLunchButton ?? true;

    if (message) {
        return (
            <div className="flex flex-col items-center justify-center h-screen bg-gray-100 dark:bg-gray-900 text-gray-900 dark:text-white select-none">
                <h1 className="text-5xl font-bold animate-pulse">{message}</h1>
            </div>
        );
    }

    const displayName = `${employee.firstName} ${employee.lastName}`.trim();
    const orientationChecklistUrl = "https://staff.metropei.com/orientation-checklist/";

    return (
        <div className="flex flex-col items-center justify-center min-h-screen bg-gray-100 dark:bg-gray-900 p-4 select-none">
            <button onClick={() => navigate('/')} className="absolute top-8 left-8 text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition">
                <ChevronLeftIcon className="w-8 h-8" />
            </button>
            <div className="absolute top-8 right-8">
                <Button variant="secondary" onClick={() => setIsPinModalOpen(true)}>
                    <LockIcon className="w-5 h-5" />
                    Change PIN
                </Button>
            </div>
            <div className="text-center mb-8">
                <h1 className="text-4xl md:text-6xl font-bold text-gray-900 dark:text-white mb-2">Welcome, {displayName}!</h1>
                <p className="text-xl md:text-2xl text-gray-500 dark:text-gray-400">
                    Your current status is:
                    <span className={`font-bold ml-2 ${
                        currentStatus === EmployeeStatus.CLOCKED_IN ? 'text-green-500 dark:text-green-400' :
                        currentStatus === EmployeeStatus.ON_BREAK ? 'text-yellow-500 dark:text-yellow-400' :
                        'text-red-500 dark:text-red-400'
                    }`}>
                        {currentStatus.replace('_', ' ')}
                    </span>
                </p>
            </div>

            {!employee.email && (
                <div className="mb-8 bg-yellow-100 dark:bg-yellow-900/50 rounded-2xl p-6 shadow-lg border border-yellow-500 dark:border-yellow-700 w-full max-w-2xl flex flex-col md:flex-row items-center gap-6">
                    <div className="flex-1 text-left">
                        <h3 className="text-xl font-bold text-yellow-900 dark:text-yellow-200">Important: Update Your Profile Information</h3>
                        <p className="mt-2 text-yellow-800 dark:text-yellow-300">
                            There is missing or incorrect information in your profile. Please go to the orientation checklist page as soon as possible to make sure your payroll information is accurate.
                        </p>
                        <p className="mt-3 inline-block text-sm text-brand-secondary">
                            {orientationChecklistUrl}
                        </p>
                    </div>
                    <div className="flex-shrink-0 text-center">
						<img 
							src="/qrcode.png"
							alt="QR code for orientation checklist" 
							className="w-36 h-36 rounded-lg bg-white p-1 shadow-md"
						/>
                        <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">Scan to update</p>
                    </div>
                </div>
            )}

            {error && (
                <div className="mb-8 text-center bg-red-800/50 rounded-2xl p-4 shadow-lg border border-red-700 w-full max-w-md">
                    <p className="text-lg text-white font-bold">
                        {error}
                    </p>
                </div>
            )}

            {(currentStatus === EmployeeStatus.CLOCKED_IN || currentStatus === EmployeeStatus.ON_BREAK) && (
                <div className="mb-8 text-center bg-white/50 dark:bg-gray-800/50 rounded-2xl p-6 shadow-lg border border-gray-200 dark:border-gray-700 w-full max-w-md">
                    <p className="text-lg text-gray-600 dark:text-gray-300 uppercase tracking-widest mb-2">
                        {currentStatus === EmployeeStatus.CLOCKED_IN ? 'Time Clocked In' : 'Time on Lunch'}
                    </p>
                    <p className="text-6xl font-mono font-bold text-gray-900 dark:text-white tracking-wider">
                        {formatDuration(elapsedTime, false)}
                    </p>
                </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 w-full max-w-2xl">
                <Button
                    onClick={() => onClockInOut()}
                    disabled={isLoading || currentStatus === EmployeeStatus.ON_BREAK}
                    className={`h-32 text-3xl ${!showLunchButton ? 'md:col-span-2' : ''}`}
                    variant={currentStatus === EmployeeStatus.CLOCKED_OUT ? 'primary' : 'danger'}
                >
                    {getClockButtonText()}
                </Button>
                {showLunchButton && (
                    <Button
                        onClick={() => onBreak()}
                        disabled={isLoading || currentStatus === EmployeeStatus.CLOCKED_OUT}
                        className="h-32 text-3xl"
                        variant="secondary"
                    >
                        {getBreakButtonText()}
                    </Button>
                )}
            </div>
            <div className="mt-6 w-full max-w-2xl">
                <Button
                    onClick={() => navigate('/my-report', { state: { employee } })}
                    variant="secondary"
                    size="lg"
                    className="w-full"
                >
                    <FileTextIcon />
                    View Timecard Report
                </Button>
            </div>
			<div className="mt-6 w-full max-w-2xl">
			    <Button
				    onClick={() => navigate('/')} 
					variant="danger"
					size="lg"
					className="w-full"
				>
                    <ChevronLeftIcon />
					Cancel
                </Button>
			</div>
			
			{employee.leaveBalances && employee.leaveBalances.length > 0 && (
                <div className="mt-6 w-full max-w-4xl py-4">
                    <h2 className="text-2xl font-bold text-center mb-4 text-gray-700 dark:text-gray-300">Leave Balances</h2>
                    <div className="flex flex-wrap justify-center gap-4">
                        {employee.leaveBalances.map((balance, index) => (
                            <LeaveBalanceCard 
                                key={`${balance.leaveTypeId}-${balance.fromDate}`} 
                                balance={balance} 
                                index={index}
                                onClick={() => handleLeaveCardClick(balance)}
                            />
                        ))}
                    </div>
                </div>
            )}

            <PINModal
                isOpen={isPinModalOpen}
                onClose={() => setIsPinModalOpen(false)}
                onSave={handleSavePin}
                currentPin={employee.pin}
            />
            
            {selectedLeaveBalance && (
                <LeaveDetailsModal
                    isOpen={isLeaveDetailsModalOpen}
                    onClose={() => setIsLeaveDetailsModalOpen(false)}
                    employeeId={employee.id}
                    balance={selectedLeaveBalance}
                />
            )}

            <ManualTimeEntryModal
                isOpen={isManualTimeModalOpen}
                onClose={() => setIsManualTimeModalOpen(false)}
                onSave={handleManualTimeSave}
                title={getManualModalTitle()}
            />
        </div>
    );
};

export default EmployeeDashboardScreen;